import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type AgentScope = 'pipeline' | 'company' | 'deal' | 'task' | 'tasks';

export type AgentContextValue = Readonly<{
  scope: AgentScope;
  companyId?: string;
  companyName?: string;
  dealId?: string;
  dealTitle?: string;
  taskId?: string;
  taskTitle?: string;
}>;

export const PIPELINE_CONTEXT: AgentContextValue = { scope: 'pipeline' };

type AgentContextStore = Readonly<{
  context: AgentContextValue;
  set(next: AgentContextValue): void;
}>;

const Store = createContext<AgentContextStore | undefined>(undefined);

function sameContext(left: AgentContextValue, right: AgentContextValue): boolean {
  return left.scope === right.scope
    && left.companyId === right.companyId
    && left.dealId === right.dealId
    && left.taskId === right.taskId
    && left.companyName === right.companyName
    && left.dealTitle === right.dealTitle
    && left.taskTitle === right.taskTitle;
}

/**
 * Remembers which screen the user came from, so the agent opens with the
 * context of that company / deal / task instead of a hard-coded one.
 */
export function AgentContextProvider(input: { children: ReactNode; initial?: AgentContextValue }): ReactNode {
  const [context, setContext] = useState<AgentContextValue>(input.initial ?? PIPELINE_CONTEXT);
  const set = useCallback((next: AgentContextValue) => {
    setContext((current) => sameContext(current, next) ? current : next);
  }, []);
  const value = useMemo<AgentContextStore>(() => ({ context, set }), [context, set]);
  return <Store.Provider value={value}>{input.children}</Store.Provider>;
}

export function useAgentContext(): AgentContextStore {
  const value = useContext(Store);
  if (!value) throw new Error('useAgentContext must be used inside AgentContextProvider');
  return value;
}

/** «Контекст: Orion Demo → сделка «Orion Demo — Company Brain»». */
export function describeAgentContext(context: AgentContextValue): string {
  if (context.scope === 'deal' && context.dealTitle) {
    return context.companyName
      ? `${context.companyName} → сделка «${context.dealTitle}»`
      : `сделка «${context.dealTitle}»`;
  }
  if (context.scope === 'company' && context.companyName) return context.companyName;
  if (context.scope === 'task' && context.taskTitle) {
    return context.companyName
      ? `${context.companyName} → задача «${context.taskTitle}»`
      : `задача «${context.taskTitle}»`;
  }
  if (context.scope === 'tasks') return 'Все задачи';
  return 'Вся воронка';
}

/** Short prefix the agent receives with every turn started from this screen. */
export function agentContextPrompt(context: AgentContextValue): string | undefined {
  const parts: string[] = [];
  if (context.companyName) parts.push(`компания «${context.companyName}»`);
  if (context.dealTitle) parts.push(`сделка «${context.dealTitle}»`);
  if (context.taskTitle) parts.push(`задача «${context.taskTitle}»`);
  if (context.scope === 'pipeline') parts.push('вся воронка продаж');
  if (context.scope === 'tasks') parts.push('раздел задач');
  if (!parts.length) return undefined;
  return `Контекст экрана: ${parts.join(', ')}.`;
}
